import { Link, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  Settings, 
  LogOut,
  ShieldCheck,
  Ticket,
  Receipt,
  FileText,
  Tag,
  DollarSign,
  CreditCard,
  ArrowLeft
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Permission } from "@/lib/permissions";
import { useAuth } from "@/contexts/AuthContext";
import { PermissionGate } from "./PermissionGate";

import { useTranslation } from "react-i18next";

interface AdminSidebarProps {
  onItemClick?: () => void;
}

export function AdminSidebar({ onItemClick }: AdminSidebarProps = {}) {
  const location = useLocation();
  const { signOut } = useAuth();
  const { t } = useTranslation();

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const navItems = [
    {
      label: t('admin.sidebar.overview'),
      path: "/admin",
      icon: LayoutDashboard,
      exact: true,
      permission: "admin.read" 
    },
    {
      label: t('admin.sidebar.users'),
      path: "/admin/users",
      icon: Users,
      permission: "users.read"
    },
    {
      label: t('admin.sidebar.tickets'),
      path: "/admin/tickets",
      icon: Ticket,
      permission: "tickets.read"
    },
    {
      label: t('admin.sidebar.content'),
      path: "/admin/content",
      icon: FileText,
      permission: "content.read"
    },
    {
      label: t('admin.sidebar.sales'),
      path: "/admin/sales",
      icon: Receipt,
      permission: "finance.read"
    },
    {
      label: t('admin.sidebar.promo'),
      path: "/admin/promo-codes",
      icon: Tag,
      permission: "finance.read" 
    },
    {
      label: t('admin.sidebar.finance'),
      path: "/admin/finance",
      icon: DollarSign,
      permission: "finance.read"
    },
    {
      label: t('admin.sidebar.subscriptions'),
      path: "/admin/subscriptions",
      icon: CreditCard,
      permission: "finance.write"
    },
    {
      label: t('admin.sidebar.work'),
      path: "/admin/management",
      icon: ShieldCheck,
      permission: "*" 
    },
    {
      label: t('admin.sidebar.settings'),
      path: "/admin/settings",
      icon: Settings,
      permission: "admin.read"
    },
  ];

  return (
    <div className="flex h-full flex-col bg-[#F5F0E8] p-4">
      {/* Logo Area */}
      <div className="mb-8 flex items-center gap-3 px-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-black bg-[#FFD93D] text-lg font-bold shadow-[4px_4px_0_#000]">
          V
        </div>
        <div>
          <h1 className="text-xl font-black italic tracking-tighter font-serif">Vesper</h1>
          <span className="text-[10px] font-black uppercase tracking-widest text-gray-500">Admin Area</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-2">
        {navItems.map((item) => {
          const active = item.exact 
            ? location.pathname === item.path 
            : isActive(item.path);
            
          return (
            <PermissionGate key={item.path} permission={item.permission as Permission} fallback={null}>
              <Link
                to={item.path}
                onClick={onItemClick}
                className={cn(
                  "flex items-center gap-3 rounded-lg border-2 px-3 py-2.5 font-black transition-all",
                  active
                    ? "border-black bg-white shadow-[4px_4px_0_#000]"
                    : "border-transparent text-gray-600 hover:border-black hover:bg-white/50"
                )}
              >
                <item.icon className={cn("h-5 w-5", active ? "text-black" : "text-gray-500")} />
                {item.label}
              </Link>
            </PermissionGate>
          );
        })}
      </nav>

      {/* Footer / User Info */}
      <div className="mt-auto border-t-2 border-black/10 pt-4 space-y-2">
        <Link
          to="/dashboard"
          onClick={onItemClick}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 font-black text-gray-600 transition-colors hover:bg-white hover:text-black hover:border-black border-2 border-transparent hover:shadow-[4px_4px_0_#000]"
        >
          <ArrowLeft className="h-5 w-5" />
          {t('admin.sidebar.exit')}
        </Link>
        
        <button
          onClick={() => signOut()}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 font-black text-gray-600 transition-colors hover:bg-red-100 hover:text-red-600 border-2 border-transparent"
        >
          <LogOut className="h-5 w-5" />
          {t('admin.sidebar.logout')}
        </button>
      </div>
    </div>
  );
}
